<template>
  <div>
    <div v-if="advanceMode">
      <div class="d-flex justify-content-between align-items-center">
        <section-title
          bg-icon="white"
          container-class="mt-1"
          content="Favoritos"
          icon="fas fa-star font-16 text-warning"
        />

        <div v-if="advanceMode" class="view-toggle">
          <button
            class="view-toggle-btn"
            :class="{ active: viewMode === 'list' }"
            type="button"
            @click="setViewMode('list')"
          >
            <i class="fas fa-list" />
          </button>
          <button
            class="view-toggle-btn"
            :class="{ active: viewMode === 'grid' }"
            type="button"
            @click="setViewMode('grid')"
          >
            <i class="fas fa-th-large" />
          </button>
        </div>
      </div>

      <div v-if="optionFavoriteList.length > 0">
        <square-option
          v-if="filteredOptionFavoriteList.length > 0"
          :menu="filteredOptionFavoriteList"
          show-star
          :view-mode="effectiveViewMode"
          @remove="removeToFavorites"
        />
        <div v-else class="d-block ml-auto mr-auto mb-2">
          <empty
            icon="fal fa-search"
            text="No se encontraron resultados para su búsqueda."
          />
        </div>
      </div>
      <div v-else class="d-block ml-auto mr-auto mb-2">
        <empty
          icon="fal fa-lightbulb-exclamation"
          text="Puede oprimir la estrella en las tarjetas para agregarlas como favoritos."
        />
      </div>

      <div id="border" />
    </div>

    <square-option
      v-if="filteredOptionList.length > 0"
      :menu="filteredOptionList"
      :show-star="advanceMode"
      :view-mode="effectiveViewMode"
      @add="addToFavorites"
      @remove="removeToFavorites"
    />
    <div v-else-if="hasSearch" class="d-block ml-auto mr-auto mb-2">
      <empty
        icon="fal fa-search"
        text="No se encontraron resultados para su búsqueda."
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { name as userModuleName } from '@/modules/user/store';
import squareOption from '@/components/common/menu/SquareOption';
import SectionTitle from 'SectionTitle';
import Empty from '@/components/common/Empty.vue';

import { addNavToFavorites, removeNavToFavorites } from '@/core/auth';
import { getUserMenu } from '@/modules/user/api';

const VIEW_MODE_KEY = 'menuViewMode';
const VIEW_MODES = ['grid', 'list'];

/** Marcas diacríticas combinantes (U+0300 a U+036F) */
const DIACRITICS = /[\u0300-\u036f]/g;

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .toLowerCase();

const allowNavigation = () => true;

export default {
  name: 'MenuOptions',
  meta: {
    description:
      'Cuerpo de los menús de módulo: favoritos, selector de vista y tarjetas de opciones.',
  },
  components: {
    squareOption,
    SectionTitle,
    Empty,
  },
  props: {
    menuTitle: {
      type: String,
      required: true,
      meta: {
        description: 'Título del módulo en el mapa del sitio',
        example: 'navMenuTitles.Pei',
      },
    },
    search: {
      type: String,
      default: '',
      meta: {
        description: 'Texto por el cual se filtran las opciones',
        example: 'this.searchQuery',
      },
    },
    canNavigate: {
      type: Function,
      default: null,
      meta: {
        description:
          'Validación que se ejecuta antes de navegar a una opción. Debe retornar una promesa booleana.',
        example: 'this.validateStudent',
      },
    },
    advanceMode: {
      type: Boolean,
      default: false,
      meta: {
        description:
          'Muestra favoritos y el selector de vista lista/tarjetas. Depende del parámetro de sistema ProviderMenuAdvanceModeEnabled.',
        example: 'true o false',
      },
    },
  },
  data() {
    return {
      viewMode: localStorage.getItem(VIEW_MODE_KEY) || 'grid',
    };
  },
  computed: {
    ...mapState(userModuleName, ['userMenu']),
    optionList() {
      const menuModule = this.userMenu?.find((x) => x.title === this.menuTitle);
      return (menuModule?.children || []).map(this.mapOption);
    },
    optionFavoriteList() {
      const menuModule = this.userMenu?.find((x) => x.title === this.menuTitle);
      return (menuModule?.children || [])
        .map(this.mapOption)
        .filter((x) => x.isFavorite);
    },
    filteredOptionList() {
      const options = this.advanceMode
        ? this.optionList.filter((option) => !option.isFavorite)
        : this.optionList;
      return this.filterByTitle(options);
    },
    filteredOptionFavoriteList() {
      return this.filterByTitle(this.optionFavoriteList);
    },
    hasSearch() {
      return this.search.trim().length > 0;
    },
    /**
     * Sin modo avanzado no hay selector de vista, por lo que siempre se
     * muestran las tarjetas aunque el usuario haya guardado la vista de lista.
     */
    effectiveViewMode() {
      return this.advanceMode ? this.viewMode : 'grid';
    },
  },
  methods: {
    ...mapActions(userModuleName, ['setMenu']),
    mapOption(item) {
      return {
        id: item.id,
        title: item.title,
        description: item.description,
        detail: item.detail,
        icon: item.iconClasses,
        mainImage: item.mainImagePath,
        hoverImage: item.mainImageHoverPath,
        needSelectedStudent: item.needSelectedStudent,
        route: item.navigationPath,
        canNavigate: this.canNavigate || allowNavigation,
        trackingFlag: item.trackingFlag,
        isFavorite: item.isFavorite,
      };
    },
    filterByTitle(options) {
      const query = normalizeText(this.search.trim());
      if (!query) {
        return options;
      }
      return options.filter((item) =>
        normalizeText(item.title).includes(query)
      );
    },
    setViewMode(mode) {
      if (!VIEW_MODES.includes(mode)) {
        return;
      }
      this.viewMode = mode;
      localStorage.setItem(VIEW_MODE_KEY, mode);
    },
    async addToFavorites(item) {
      if (!this.advanceMode) {
        return;
      }
      await addNavToFavorites(item.id);
      await this.refreshMenu();
    },
    async removeToFavorites(item) {
      if (!this.advanceMode) {
        return;
      }
      await removeNavToFavorites(item.id);
      await this.refreshMenu();
    },
    async refreshMenu() {
      const userMenu = await getUserMenu();
      if (userMenu && userMenu.length) {
        this.setMenu(userMenu);
      }
    },
  },
};
</script>

<style scoped>
.view-toggle {
  display: flex;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  overflow: hidden;
}

.view-toggle-btn {
  background: #f2f2f2;
  color: #3f5b60;
  border: none;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}

.view-toggle-btn.active {
  background: rgb(63, 91, 96);
  color: #fff;
}

#border {
  height: 1px;
  background: yellow;
  text-align: center;
  line-height: 100px;
  background: -webkit-gradient(
      linear,
      left top,
      right top,
      color-stop(50%, orange),
      color-stop(0%, rgba(255, 255, 255, 0))
    ),
    -webkit-gradient(linear, left top, left bottom, color-stop(50%, blue), color-stop(0%, rgba(255, 255, 255, 0))),
    -webkit-gradient(linear, left top, right top, color-stop(50%, green), color-stop(0%, rgba(255, 255, 255, 0))),
    -webkit-gradient(linear, left top, left bottom, color-stop(50%, red), color-stop(0%, rgba(255, 255, 255, 0)));
  background: linear-gradient(to right, #4caf50 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(#4caf50 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(to right, green 50%, rgba(255, 255, 255, 0) 0%),
    linear-gradient(#4caf50 50%, rgba(255, 255, 255, 0) 0%);
  background-position: top, right, bottom, left;
  background-repeat: repeat-x, repeat-y;
  background-size: 10px 1px, 1px 10px;
  margin-top: 50px;
}

::v-deep .tag-badge-info {
  background: linear-gradient(to right, white, #e2e2e2);
}

::v-deep .dot {
  background-color: #fff !important;
}
</style>
